import React from "react"
import {v4 as uuid} from "uuid"
import {isEmpty} from "lodash"
import {withTranslation} from "react-i18next"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableProps} from "types/common"
import {handleError, checkPermission, toastError, toastWarning, scrollTableToBottom, toastSuccess} from "helpers"
import {settingAcademicTrackService} from "services"
import {KlassappTableHOC} from "HOC"
import {BaseDepartmentId} from "types/departments"
import {PermissionsRequired} from "components/PermissionsRequired"
import {Permissions, PermissionUserType} from "types/permission"
import {getPermissionsByDepartment} from "sections/Settings/helper"
import {Model} from "Model"
import styles from "./AcademicTrack.module.css"
import {Icon} from "components"
import {Button, Checkbox} from "antd"
import {AcademicTrackPhasesAndShifts} from "../AcademicTrackPhasesAndShifts"
import moment from "moment"
import {ResyncPhaseAndShiftPopup} from "./ResyncPhaseAndShiftPopup"

type PageProps = {
    t: Function
    model: Model
}

type Props = KlassappTableProps & PageProps

type State = {
    newItemData: any
    oldItemData: any
    isSubmitting: boolean
    permissions: PermissionUserType
    selectedAcademicTrack: any
    resyncPopup: {
        isVisible: boolean
        academicTrackIds: number[]
        campuses: any[]
        programs: any[]
        programVersions: any[]
        startDate: any
    }
}

class AcademicTrackTab extends React.Component<Props, State> {
    state = {
        newItemData: null,
        oldItemData: null,
        isSubmitting: false,
        permissions: {staff: []},
        selectedAcademicTrack: null,
        resyncPopup: {
            isVisible: false,
            academicTrackIds: [],
            campuses: [],
            programs: [],
            programVersions: [],
            startDate: null
        }
    }

    componentDidMount() {
        this.getPermissions()
        this.props.dispatchFunc([
            {key: "getPageTitle", func: this.getPageTitle},
            {key: "getListData", func: this.getData},
            {key: "getFields", func: this.getFields},
            {key: "getColumns", func: this.getColumns},
            {key: "getTableHeaderActions", func: this.getTableHeaderActions},
            {key: "getMenuActions", func: this.getMenuActions},
            {key: "onClickDeleteMulti", func: this.onClickDeleteMulti},
            {key: "onClickDuplicateMulti", func: this.onClickDuplicateMulti},
            {key: "onClickEdit", func: this.onClickEdit},
            {key: "onClickDelete", func: this.onClickDelete}
        ])
        this.getData()
    }

    getPermissions = () => {
        const permissions = getPermissionsByDepartment(BaseDepartmentId.Academics)
        this.setState({permissions})
    }

    getPageTitle = () => {
        return this.props.t("settings.academicTrack")
    }

    getFields = () => {
        const {t} = this.props
        return [
            t("settings.academicTrack"),
            t("settings.description"),
            t("settings.isActive"),
            "View Phases And Shifts",
            "Resync Phases And Shifts"
        ]
    }

    getTableHeaderActions = (isShowDuplicateBtn = true, checkedData = []) => {
        const canWrite = checkPermission(this.state.permissions, this.props.model)
        if (!canWrite) {
            return []
        }
        const hasLockedItem = (checkedData || []).some((item) => item.isLocked)
        const {t, onClickShowConfirmModal} = this.props
        const actions = []
        if (!hasLockedItem) {
            actions.push({
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            })
        }
        if (isShowDuplicateBtn) {
            actions.push({
                title: t("common:action.duplicate"),
                icon: "DUPLICATE",
                action: () => this.onClickDuplicateMulti()
            })
        }
        return actions
    }

    canShowDeleteIcon = (data) => {
        return checkPermission(this.state.permissions, this.props.model) && !data.isLocked
    }

    canShowEditIcon = () => {
        return checkPermission(this.state.permissions, this.props.model)
    }

    getMenuActions = () => {
        const {t} = this.props
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: this.onClickEdit,
                canShow: this.canShowEditIcon
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: this.onClickDelete,
                canShow: this.canShowDeleteIcon
            }
        ]
    }

    getData = async () => {
        this.props.dispatch({isLoading: true})
        const params = this.getParams()
        try {
            const {data, total} = await settingAcademicTrackService.getAllAcademicTracks(params)
            const newData = data.map((item) => ({
                ...item,
                id: item.trackId,
                nameHtml: this.renderNameHtml(item),
                isActiveHtml: this.renderIsActiveHtml(true, !!item.isActive),
                viewPhasesAndShiftsBtn: this.renderViewPhasesAndShiftsBtn(item),
                resyncPhasesAndShiftsBtn: this.renderResyncPhasesAndShiftsBtn(item)
            }))
            this.props.dispatch({data: newData, total})
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    renderViewPhasesAndShiftsBtn = (item) => {
        return (
            <BaseButton title={"View Phases And Shifts"} onClick={() => this.setState({selectedAcademicTrack: item})} />
        )
    }

    renderResyncPhasesAndShiftsBtn = (item) => {
        return (
            <BaseButton
                variant={"secondary"}
                disabled={!item.isActive}
                title={"Resync Phases And Shifts"}
                onClick={() => this.openResyncPopup([item.trackId])}
            />
        )
    }

    openResyncPopup = (academicTrackIds: number[]) => {
        this.setState((prevState) => ({
            ...prevState,
            resyncPopup: {
                isVisible: true,
                academicTrackIds,
                campuses: [],
                programs: [],
                programVersions: [],
                startDate: null
            }
        }))
    }

    closeResyncPopup = () => {
        this.setState((prevState) => ({
            ...prevState,
            resyncPopup: {
                ...prevState.resyncPopup,
                isVisible: false
            }
        }))
    }

    onChangeResyncPopup = (key: string, value: any) => {
        this.setState((prevState) => {
            const popup = {...prevState.resyncPopup, [key]: value}
            if (key === "campuses") {
                popup.programs = []
                popup.programVersions = []
            }
            if (key === "programs") {
                popup.programVersions = []
            }
            return {
                ...prevState,
                resyncPopup: popup
            }
        })
    }

    onConfirmResyncPopup = async () => {
        const {resyncPopup} = this.state
        const campusIds = (resyncPopup.campuses || []).map((item) => item.id).filter(Boolean)
        const programIds = (resyncPopup.programs || []).map((item) => item.id).filter(Boolean)
        const programVersionIds = (resyncPopup.programVersions || []).map((item) => item.id).filter(Boolean)

        if (!campusIds.length) {
            toastError("Campus is required.")
            return
        }

        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingAcademicTrackService.resyncAcademicTrackPhasesAndShifts({
                academicTrackIds: resyncPopup.academicTrackIds,
                campusIds,
                programIds,
                programVersionIds,
                startDate: resyncPopup.startDate ? moment(resyncPopup.startDate).format("YYYY-MM-DD") : undefined
            })
            this.closeResyncPopup()
            toastSuccess("Sync process started successfully and will take a few moments to complete.")
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    renderResyncPopup = () => {
        const {resyncPopup, isSubmitting} = this.state

        // Get selected academic track names for display
        const selectedAcademicTrackNames =
            this.props.data
                ?.filter((item) => resyncPopup.academicTrackIds.includes(item.trackId))
                .map((item) => item.name) || []

        return (
            <ResyncPhaseAndShiftPopup
                isVisible={resyncPopup.isVisible}
                isSubmitting={isSubmitting}
                resyncPopup={resyncPopup}
                selectedAcademicTrackNames={selectedAcademicTrackNames}
                onClose={this.closeResyncPopup}
                onChange={this.onChangeResyncPopup}
                onConfirm={this.onConfirmResyncPopup}
            />
        )
    }

    renderNameHtml = (item) => {
        return (
            <div className={styles.titleWrap}>
                {item.isLocked ? (
                    <div className={styles.lockIcon}>
                        <Icon icon="LOCK_CHECKMARK" />
                    </div>
                ) : (
                    ""
                )}
                <span className={styles.title}>{item.name}</span>
            </div>
        )
    }

    renderIsActiveHtml = (disabled = true, value?: boolean) => {
        const newItemData = this.state.newItemData || {}

        if (disabled) {
            return (
                <div className={styles.inputWrap}>
                    <div className={styles.visibleCheckboxWrap}>
                        <Checkbox disabled checked={value} />
                    </div>
                </div>
            )
        }

        return (
            <div className={styles.inputWrap}>
                <div className={styles.visibleCheckboxWrap}>
                    <Checkbox
                        checked={newItemData.isActiveData}
                        onChange={(event) => this.onChangeNewItemData("isActiveData", event.target.checked)}
                    />
                </div>
            </div>
        )
    }

    getParams = () => {
        const {page, pageSize, orderField} = this.props
        const params: any = {
            range: {
                page,
                pageSize
            }
        }
        if (!isEmpty(orderField)) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order
            }
        }
        return params
    }

    getColumns = () => {
        const {t} = this.props

        return [
            {
                title: t("settings.academicTrack"),
                field: "nameHtml",
                style: {minWidth: "200px"},
                sortable: true,
                orderField: "name"
            },
            {
                title: t("settings.description"),
                field: "description",
                style: {minWidth: "200px"},
                sortable: true
            },
            {
                title: t("settings.isActive"),
                field: "isActiveHtml",
                style: {minWidth: "80px"}
            },
            {
                title: "View Phases And Shifts",
                field: "viewPhasesAndShiftsBtn"
            },
            {
                title: "Resync Phases And Shifts",
                field: "resyncPhasesAndShiftsBtn"
            }
        ]
    }

    onClickDeleteMulti = async () => {
        const checkedItems = this.props.data.filter((item) => item.isChecked)
        const payload = checkedItems.map((item) => item.id)
        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingAcademicTrackService.deleteAcademicTrack(payload)
            await this.getData()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onClickDuplicateMulti = async () => {
        const {data} = this.props
        const payload = data
            .filter((item) => item.isChecked)
            .map(({name, description}) => ({
                name: `${name} (Copy)`,
                description
            }))
        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await settingAcademicTrackService.duplicateAcademicTrack(payload)
            await this.getData()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onClickEdit = (editItem) => {
        const {newItemData} = this.state
        if (newItemData) {
            toastWarning(this.props.t("common:validation.saveBeforeEdit"))
            return
        }
        this.setState(
            {
                newItemData: {
                    ...editItem,
                    nameData: editItem.name,
                    descriptionData: editItem.description,
                    isActiveData: editItem.isActive
                },
                oldItemData: {...editItem}
            },
            () => {
                const newData = this.props.data.map((item) => {
                    if (item.id === editItem.id) {
                        item.nameHtml = this.renderNameInput()
                        item.description = this.renderDescriptionInput()
                        item.isActiveHtml = this.renderIsActiveHtml(false)
                        return item
                    }
                    return item
                })
                this.props.dispatch({data: newData})
            }
        )
    }

    onClickDelete = async (deletedItem) => {
        if (deletedItem.isForm) {
            this.setState({newItemData: null, oldItemData: null})
            await this.getData()
            return
        }
        try {
            this.props.dispatch({isLoading: true})
            this.setState({newItemData: null, oldItemData: null})
            await settingAcademicTrackService.deleteAcademicTrack([deletedItem.id])
            await this.getData()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onChangeNewItemData = (key, value) => {
        const {newItemData} = this.state
        newItemData[key] = value
        const newData = this.props.data.map((item) => {
            if (item.id === newItemData.id) {
                item.nameHtml = this.renderNameInput()
                item.description = this.renderDescriptionInput()
                item.isActiveHtml = this.renderIsActiveHtml(false)
                return item
            }
            return item
        })
        this.setState({newItemData})
        this.props.dispatch({data: newData})
    }

    renderNameInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.nameData}
                placeholder="Name"
                onChange={(value) => this.onChangeNewItemData("nameData", value)}
            />
        )
    }

    renderDescriptionInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.descriptionData}
                placeholder="Description"
                onChange={(value) => this.onChangeNewItemData("descriptionData", value)}
            />
        )
    }

    onClickAddItem = () => {
        const newItem = {
            id: uuid(),
            nameHtml: this.renderNameInput(),
            description: this.renderDescriptionInput(),
            isForm: true,
            isActiveHtml: this.renderIsActiveHtml(false)
        }
        const {data} = this.props
        data.push(newItem)
        this.setState({newItemData: newItem, oldItemData: null})
        this.props.dispatch({data})
        scrollTableToBottom(this.props.tableRef)
    }

    validateDataBeforeSubmit = () => {
        const {nameData, descriptionData} = this.state.newItemData
        const {t} = this.props
        if (!nameData) {
            toastError(
                t("common:validation.cantEmpty", {
                    field: t("settings.academicTrack")
                })
            )
            return false
        }
        if (!descriptionData) {
            toastError(
                t("common:validation.cantEmpty", {
                    field: t("settings.description")
                })
            )
            return false
        }
        return true
    }

    onClickSave = async () => {
        if (!this.validateDataBeforeSubmit()) {
            return
        }
        const {nameData, descriptionData, isForm, id, isActiveData} = this.state.newItemData
        if (isForm) {
            const submitData = {
                name: nameData,
                description: descriptionData,
                isActive: !!isActiveData
            }
            await this.createActivity(submitData)
        } else {
            const submitData = {name: nameData, description: descriptionData, isActive: !!isActiveData}
            await this.updateActivity(id, submitData)
        }
        this.setState({newItemData: null, oldItemData: null}, () => {
            this.getData()
        })
    }

    createActivity = async (data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await settingAcademicTrackService.createAcademicTrack(data)
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    updateActivity = async (id, data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await settingAcademicTrackService.updateAcademicTrack(id, data)
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    onClickCancel = () => {
        const {oldItemData} = this.state
        const newData = this.props.data
            .filter((item) => !item.isForm)
            .map((item) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.nameHtml = oldItemData.name
                    item.description = oldItemData.description
                    item.isActiveHtml = this.renderIsActiveHtml(true, oldItemData.isActive)
                    return item
                }
                return item
            })
        this.setState({newItemData: null, oldItemData: null})
        this.props.dispatch({data: newData})
    }

    onClickBackPhasesAndShifts = () => {
        this.setState({
            ...this.state,
            selectedAcademicTrack: null
        })
    }

    render() {
        const {newItemData, isSubmitting} = this.state
        const {
            t,
            page,
            total,
            pageSize,
            columns,
            data,
            menuActions,
            allFields,
            fields,
            tableHeaderActions,
            isLoading,
            isHideMenuActions,
            isShowTableHeaderAction,
            orderField
        } = this.props

        return (
            <div className={styles.wrapper}>
                {this.state.selectedAcademicTrack ? (
                    <div className={styles.phaseAndShiftsTemplate}>
                        <Button
                            onClick={this.onClickBackPhasesAndShifts}
                            icon={<Icon icon="BACK" color="#FF349B" />}
                            className={styles.backBtnIcon}
                        />
                        <AcademicTrackPhasesAndShifts academicTrack={this.state.selectedAcademicTrack} />
                    </div>
                ) : (
                    <>
                        <KlassappTableHeader
                            isShowAction={isShowTableHeaderAction}
                            actions={tableHeaderActions}
                            page={page}
                            total={total}
                            defaultPageSize={pageSize}
                            onChangePage={this.props.onChangePage}
                            onChangeRowPerPage={this.props.onChangeRowPerPage}
                            fields={fields}
                            allFields={allFields}
                            onChangeFields={this.props.onChangeFields}
                            onChangeAllFields={this.props.onChangeAllFields}
                            onDraggableColumn={this.props.onDraggableColumn}
                        />
                        <KlassappTable
                            columns={columns}
                            data={data}
                            menuActions={isHideMenuActions ? [] : menuActions}
                            isLoading={isLoading}
                            fields={fields}
                            allFields={allFields}
                            orderField={orderField}
                            isShowCheckedColumn
                            tableRef={this.props.tableRef}
                            keyRender="trackId"
                            onClickRowItem={() => {}}
                            onChangeFields={this.props.onChangeFields}
                            onUpdateRowData={this.props.onUpdateRowData}
                            onUpdateTableData={this.props.onUpdateTableData}
                            onClickSortColumn={this.props.onClickSortColumn}
                            onDraggableColumn={this.props.onDraggableColumn}
                            onChangeAllFields={this.props.onChangeAllFields}
                        />
                        <PermissionsRequired permissions={{staff: [Permissions.Staff.Settings.Modules.Academics.Edit]}}>
                            {!newItemData ? (
                                !isLoading && <AddItemCircleButton onClick={this.onClickAddItem} />
                            ) : (
                                <div className={styles.buttonWrap}>
                                    <SecondaryButton
                                        title={t("common:action.cancel")}
                                        onClick={this.onClickCancel}
                                        className={styles.cancelBtn}
                                    />
                                    <BaseButton
                                        title={t("common:action.save").toUpperCase()}
                                        onClick={this.onClickSave}
                                        loading={isSubmitting}
                                    />
                                </div>
                            )}
                        </PermissionsRequired>
                        {this.renderResyncPopup()}
                    </>
                )}
            </div>
        )
    }
}

export const AcademicTrack = KlassappTableHOC(withTranslation(["academics", "common"])(AcademicTrackTab))
